import React, { useState } from "react";
import { motion } from "framer-motion";
import { Check, Sparkles, ChevronLeft, Loader2 } from "lucide-react";

const PaywallModal = ({
  isOpen,
  onSubscribe,
  isLoading,
  onSignOut,
  onBack,
  personality,
}) => {
  // Get personality-specific customization
  const getPersonalityConfig = (personalityType) => {
    const configs = {
      leader: {
        defaultBilling: "annual",
        header: "Unlock Biblical Wisdom",
        subtitle: "Fast, clear answers without distractions",
        ctaText: "Start Now →",
        emphasizeFeatures: ["Unlimited Bible questions", "No ads, no limits"],
      },
      thinker: {
        defaultBilling: "annual",
        header: "Your Bible Study Research Tool",
        subtitle: "Deep contextual understanding at your fingertips",
        ctaText: "Choose Your Plan →",
        emphasizeFeatures: ["Visual mind mapping", "Unlimited Bible questions"],
      },
      promoter: {
        defaultBilling: "monthly",
        header: "Explore and Share Bible Insights",
        subtitle: "Discover something new every day",
        ctaText: "Let's Get Started! ✨",
        emphasizeFeatures: [
          "Tap to explore deeper",
          "Save your learning journey",
        ],
      },
      nurturer: {
        defaultBilling: "annual",
        header: "Your Daily Spiritual Companion",
        subtitle: "Gentle guidance for your faith journey",
        ctaText: "Begin Your Journey →",
        emphasizeFeatures: ["No ads, no limits", "Save your learning journey"],
      },
    };
    return configs[personalityType] || configs.nurturer;
  };

  const config = getPersonalityConfig(personality);
  const [billingCycle, setBillingCycle] = useState(config.defaultBilling);

  const pricing = {
    monthly: {
      founding: 14.99,
      regular: 19.99,
    },
    annual: {
      founding: 149.99,
      regular: 149.99,
      savings: 30,
    },
  };

  const isFoundingMember = true; // TODO: Replace with actual logic
  const currentPrice =
    billingCycle === "monthly"
      ? pricing.monthly.founding
      : pricing.annual.founding;

  const handleSubscribe = () => {
    // IMPORTANT: Use PRICE IDs (price_xxx), not PRODUCT IDs (prod_xxx)
    // Find these in Stripe Dashboard → Products → Click your product → Copy the price ID
    const priceId =
      billingCycle === "monthly"
        ? "price_1SXf5PJgpMdQ68gVmkWbz5VC" // TODO: Replace with your monthly PRICE ID
        : "price_1SXf64JgpMdQ68gVSruu2uAx"; // TODO: Replace with your annual PRICE ID

    onSubscribe(priceId, billingCycle);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-60 overflow-y-auto">
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        className="relative w-full max-w-lg bg-white rounded-3xl shadow-2xl overflow-hidden my-8 max-h-screen overflow-y-auto">
        {/* Back button */}
        {onBack && (
          <button
            onClick={onBack}
            className="absolute top-4 left-4 z-10 text-white hover:text-gray-200 transition-colors flex items-center gap-1 text-sm">
            <ChevronLeft size={20} />
            <span>Back</span>
          </button>
        )}

        {/* Header with gradient */}
        <div className="bg-gradient-to-r from-blue-500 to-purple-600 p-8 text-white text-center">
          <div className="flex justify-center mb-4">
            <Sparkles size={48} className="text-yellow-300" />
          </div>
          <h2 className="text-3xl font-bold mb-3">{config.header}</h2>
          <p className="text-blue-100 leading-relaxed px-4">
            {config.subtitle}
          </p>
        </div>

        {/* Content */}
        <div className="p-8">
          {/* Billing Toggle */}
          <div className="flex items-center justify-center gap-3 mb-6">
            <button
              onClick={() => setBillingCycle("monthly")}
              className={`px-6 py-3 rounded-full font-semibold transition-all ${
                billingCycle === "monthly"
                  ? "bg-purple-600 text-white shadow-lg"
                  : "bg-gray-100 text-gray-600 hover:bg-gray-200"
              }`}>
              Monthly
            </button>
            <button
              onClick={() => setBillingCycle("annual")}
              className={`px-6 py-3 rounded-full font-semibold transition-all relative ${
                billingCycle === "annual"
                  ? "bg-purple-600 text-white shadow-lg"
                  : "bg-gray-100 text-gray-600 hover:bg-gray-200"
              }`}>
              Annual
              <span className="absolute -top-2 -right-2 bg-green-500 text-white text-xs px-2 py-1 rounded-full">
                Save ${pricing.annual.savings}
              </span>
            </button>
          </div>

          {/* Price Display */}
          <div className="text-center mb-8">
            <div className="text-5xl font-bold text-purple-600 mb-2">
              ${currentPrice}
              <span className="text-xl text-gray-500">
                /{billingCycle === "monthly" ? "mo" : "year"}
              </span>
            </div>
            {billingCycle === "annual" && (
              <p className="text-sm text-green-600 font-medium">
                Just ${(currentPrice / 12).toFixed(2)}/month
              </p>
            )}
            {isFoundingMember && (
              <p className="text-sm text-purple-600 font-semibold mt-2">
                ✨ Founding Member - Lock this price forever
              </p>
            )}
          </div>

          {/* Features */}
          <div className="space-y-3 mb-8">
            <Feature
              text="Unlimited Bible questions"
              emphasized={config.emphasizeFeatures.includes(
                "Unlimited Bible questions"
              )}
            />
            <Feature
              text="Visual mind mapping"
              emphasized={config.emphasizeFeatures.includes(
                "Visual mind mapping"
              )}
            />
            <Feature
              text="Tap to explore deeper"
              emphasized={config.emphasizeFeatures.includes(
                "Tap to explore deeper"
              )}
            />
            <Feature
              text="Save your learning journey"
              emphasized={config.emphasizeFeatures.includes(
                "Save your learning journey"
              )}
            />
            <Feature
              text="No ads, no limits"
              emphasized={config.emphasizeFeatures.includes(
                "No ads, no limits"
              )}
            />
          </div>

          {/* CTA Button */}
          <button
            onClick={handleSubscribe}
            disabled={isLoading}
            className="w-full bg-gradient-to-r from-blue-500 to-purple-600 text-white font-bold py-4 px-8 rounded-full shadow-lg hover:shadow-xl transition-all disabled:opacity-50 disabled:cursor-not-allowed mb-4 flex items-center justify-center gap-2">
            {isLoading ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                <span>Processing...</span>
              </>
            ) : (
              config.ctaText
            )}
          </button>

          {/* Trust Signals */}
          <div className="space-y-1 text-center text-xs text-gray-500">
            <p className="pb-3">🔒 Secure payment via Stripe</p>

            {/* Sign Out Option */}
            {onSignOut && (
              <div className="mt-3 pt-3 border-t border-gray-200">
                <button
                  onClick={onSignOut}
                  className="text-gray-400 hover:text-gray-600 underline transition-colors text-xs">
                  Not ready? Sign out
                </button>
              </div>
            )}
          </div>
        </div>
      </motion.div>
    </div>
  );
};

const Feature = ({ text }) => (
  <div className="flex items-center gap-3">
    <div className="flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center bg-green-100">
      <Check size={16} className="text-green-600" />
    </div>
    <span className="text-gray-700">{text}</span>
  </div>
);

export default PaywallModal;
